import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { Formik, Field } from 'formik';
import { Typography } from '@mui/material';
import { EmployeeSelectField } from '@features/employee-select';
import { ActionButton } from '@ui/index';
import {
  $assigneesReplaceByTypesData,
  $replaceEmployeeForm,
  $ticketsCountByTypesIds,
  $ticketsCountWithoutType,
  assigneesReplaceByTypes,
  getTypesForReplaceEmployee,
} from '../../../models/replace-employee';
import { EmployeeData } from '../../../models/replace-employee/types';
import * as Styled from '../styled';
import { CustomDialog, CustomDialogProps } from './dialog';
import { TypesTree } from './typesTree';

export const Body = ({
  constantReplaceEmployeeId,
  closeAction,
}: {
  constantReplaceEmployeeId?: number;
  closeAction: () => void;
}) => {
  const { t } = useTranslation();
  const [
    replaceEmployeeForm,
    ticketsCountByTypesIds,
    ticketsCountWithoutType,
    assigneesReplaceByTypesData,
  ] = useUnit([
    $replaceEmployeeForm,
    $ticketsCountByTypesIds,
    $ticketsCountWithoutType,
    $assigneesReplaceByTypesData,
  ]);
  const [dialog, setDialog] = useState<CustomDialogProps>({
    opened: false,
    title: t('SubstituteWorker.ConfirmSubstitution') as string,
    negativeBtn: {
      text: t('stories.Discard'),
    },
  });
  const formikRef = useRef();

  useEffect(() => {
    if (assigneesReplaceByTypesData.appointedEmployee === null) return;
    if (!formikRef.current) return;

    (formikRef.current as Formik).resetForm();

    if (constantReplaceEmployeeId) closeAction();
  }, [assigneesReplaceByTypesData]);

  return (
    <Formik
      ref={formikRef as any}
      initialValues={replaceEmployeeForm}
      onSubmit={() => {}}
      enableReinitialize
      render={({ values, setFieldValue }) => {
        const checkIfAppointedEqualsReplaceable = (replaceableEmployee: EmployeeData) => {
          console.log('===replaceableEmployee', replaceableEmployee);
          if (replaceableEmployee.id !== values.appointedEmployee?.id) return;

          setFieldValue('appointedEmployee', null);
        };

        const replaceableEmployeeChange = (newValue: EmployeeData | null) => {
          setFieldValue('replaceableEmployee', newValue);
          setFieldValue('types', []);

          if (!newValue) return;

          getTypesForReplaceEmployee(newValue?.id);

          checkIfAppointedEqualsReplaceable(newValue);
        };

        const isAnyChoosenType = !!values.types.length || values.withoutTypeCheckbox;
        const disableSubmit =
          !values.appointedEmployee ||
          !values.replaceableEmployee ||
          values.appointedEmployee === values.replaceableEmployee ||
          !isAnyChoosenType;

        const overAllTicketCount = values.types.reduce(
          (acc, type) => {
            return acc + (ticketsCountByTypesIds[type] ?? 0);
          },
          values.withoutTypeCheckbox ? ticketsCountWithoutType ?? 0 : 0
        );

        return (
          <>
            <CustomDialog {...dialog} setDialog={setDialog} />

            <Styled.Form
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              <Styled.FormFields>
                <Field
                  label={t('SubstituteWorker.WorkerToSubstitute')}
                  placeholder={t('SubstituteWorker.ChooseWorkerToSubstitute')}
                  component={EmployeeSelectField}
                  name="replaceableEmployee"
                  defaultId={constantReplaceEmployeeId}
                  onChange={replaceableEmployeeChange}
                  divider={false}
                  disabled={!!constantReplaceEmployeeId}
                />

                <Field
                  label={t('SubstituteWorker.WorkerToAssign')}
                  placeholder={t('SubstituteWorker.ChooseWorkerToAssign')}
                  component={EmployeeSelectField}
                  name="appointedEmployee"
                  excludeOptionsIds={
                    !!values.replaceableEmployee
                      ? [values.replaceableEmployee.id]
                      : undefined
                  }
                  divider={false}
                />

                <TypesTree values={values} setFieldValue={setFieldValue} />

                <Styled.FormField>
                  <ActionButton
                    type="button"
                    disabled={disableSubmit}
                    onClick={() =>
                      setDialog((prev) => ({
                        ...prev,
                        opened: true,
                        positiveBtn: {
                          text: t('Continue'),
                          action: () => assigneesReplaceByTypes(values),
                        },
                        text: (
                          <>
                            <div>{`${t('SubstituteWorker.YouSubstituteWorker')} ${
                              values.replaceableEmployee?.fullname
                            } ${t('SubstituteWorker.for')} ${
                              values.appointedEmployee?.fullname
                            } ${t('SubstituteWorker.in')} ${overAllTicketCount} ${t(
                              'SubstituteWorker.tickets'
                            )}.`}</div>
                            <div>{t('SubstituteWorker.ThisActionCannotBeUndone')}</div>
                          </>
                        ),
                      }))
                    }
                  >
                    {t('SubstituteWorker.SubstituteWorker')}
                  </ActionButton>

                  <Typography variant="caption" sx={{ color: '#191C2999' }}>
                    {t('SubstituteWorker.SubstituteWorkerHelpText')}
                  </Typography>
                </Styled.FormField>
              </Styled.FormFields>
            </Styled.Form>
          </>
        );
      }}
    />
  );
};
